import type { ChangeEvent, ReactNode } from 'react';
import type { IconType } from '~/components/icons';

export type SelectItem<TValue> = { label: string; value: TValue };

export type SelectFilterItem<TItemValue> = {
  label: string;
  value: string;
  icon?: IconType;
  side?: string;
  children: (props: {
    toggleOpen?: (isOpen: boolean) => void;
    handleSelectChange?: (item: Array<SelectItem<TItemValue>>) => void;
    width?: number;
    side?: string;
  }) => ReactNode;
};

export type SelectFilterItemGroup<TItemValue> = {
  label: string;
  value: string;
  filters: Array<SelectFilterItem<TItemValue>>;
};

export type SelectItems<TItemValue> =
  | SelectItem<TItemValue>
  | SelectFilterItem<TItemValue>
  | SelectFilterItemGroup<TItemValue>;

export type SelectedFilters<TItemValue> = { tag: string; values: Array<SelectItem<TItemValue>> };

export interface BaseSelectProps<TItemValue> {
  /** Sets the label of the apply button */
  applyLabel?: string;
  /** Sets the label of the back button */
  backLabel?: string;
  /** The content that will be displayed inside the popover */
  children?: ReactNode;
  /** Disables the ability to filter items */
  disableFiltering?: boolean;
  /** Sets the unselectable indeterminate state */
  indeterminateItems?: Array<SelectItem<TItemValue>>;
  /** Sets the groups of items to display */
  items: Array<SelectItems<TItemValue>>;
  /** Sets the loading state of the component */
  loading?: boolean;
  /** Sets the label that displays when theres no items */
  noDataLabel?: string;
  //** Handles the change event of the selected items */
  onSelectChange?: (item: Array<SelectItem<TItemValue>>, applyOn?: string | string[]) => void;
  /** Sets the label of the reset button */
  resetLabel?: string;
  //** The placeholder text for the search input */
  searchPlaceholder?: string;
  /** Sets the selected items */
  selectedItems?: Array<SelectItem<TItemValue>>;
  /** Limits the number of selectable items  */
  selectedLimit?: number;
  /** Handles toggling parent component  */
  toggleOpen?: (isOpen: boolean) => void;
  /** Sets the width of the popover */
  width?: number;
}

export interface BaseSelectContextProps<TItemValue> {
  applyLabel?: string;
  backLabel?: string;
  handleSearchChange: (event?: ChangeEvent<HTMLInputElement>) => void;
  handleSelectChange?: (item: Array<SelectItem<TItemValue>>, applyOn?: string | string[]) => void;
  indeterminateItems?: Array<SelectItem<TItemValue>>;
  items: Array<SelectItems<TItemValue>>;
  loading?: boolean;
  noDataLabel?: string;
  resetLabel?: string;
  resetSearchTerm: () => void;
  searchPlaceholder?: string;
  searchTerm: string;
  selectedItems: Array<SelectItem<TItemValue>>;
  selectedLimit?: number;
  toggleOpen?: (isOpen: boolean) => void;
  width?: number;
}

/**
 * Type guard to check if an array is a SelectFilterItemGroup array
 */
export function isSelectFilterItemGroupArray<TSelectItemValue>(
  items: Array<SelectItems<TSelectItemValue>>,
): items is Array<SelectFilterItemGroup<TSelectItemValue>> {
  return items.length > 0 && items.every((item): item is SelectFilterItemGroup<TSelectItemValue> => 'filters' in item);
}

/**
 * Type guard to check if an array is a SelectFilterItem array
 */
export function isSelectFilterItemArray<TSelectItemValue>(
  items: Array<SelectItems<TSelectItemValue>>,
): items is Array<SelectFilterItem<TSelectItemValue>> {
  return items.length > 0 && items.every((item) => 'children' in item);
}
